"use client"

import { useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { ChevronRight, Minus, Plus, X } from "lucide-react"
import { useApp } from "@/lib/context"
import { drinkCategoryIcon, drinkItemIcon } from "@/lib/category-icons"
import { effectivePrice, type Dish } from "@/lib/api"
import { formatPrice } from "@/lib/utils"
import DishDetailModal from "./dish-detail-modal"

/**
 * Área de bebidas — categorias em cartões compactos laranja & branco.
 * Tocar numa categoria abre o vidro com os itens; cada item abre o
 * modal de detalhes, entra no pedido pelo botão + ou ajusta a quantidade
 * diretamente pelo stepper (− 1 +).
 */
export default function BeveragesSection() {
  const { t, addToDraft, updateDraftItemQuantity, removeDraftItem, draftItems, categoriesBebidas } = useApp()
  const [categoriaAberta, setCategoriaAberta] = useState<string | null>(null)
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null)

  const categoriaAtiva = useMemo(
    () => categoriesBebidas.find((c) => c.nome === categoriaAberta) || null,
    [categoriesBebidas, categoriaAberta],
  )

  // Quantidade atual de cada bebida no rascunho (para os steppers ficarem vivos)
  const qtyPorBebida = useMemo(() => {
    const mapa = new Map<string, number>()
    for (const item of draftItems) {
      if (item?.dish?._id && item.quantity > 0) mapa.set(item.dish._id, item.quantity)
    }
    return mapa
  }, [draftItems])

  const aumentar = (dish: Dish) => {
    const atual = qtyPorBebida.get(dish._id) || 0
    if (atual === 0) addToDraft(dish)
    else updateDraftItemQuantity(dish._id, atual + 1)
  }

  const diminuir = (dish: Dish) => {
    const atual = qtyPorBebida.get(dish._id) || 0
    if (atual <= 1) removeDraftItem(dish._id)
    else updateDraftItemQuantity(dish._id, atual - 1)
  }

  return (
    <section id="bebidas" className="scroll-mt-16 bg-background py-8 sm:py-10">
      {/* Cabeçalho compacto */}
      <div className="mb-5 px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="text-[0.65rem] font-semibold uppercase tracking-[0.26em] text-primary sm:text-xs">
            Bebidas
          </span>
          <span className="h-px w-8 shrink-0 bg-primary" aria-hidden="true" />
          <span className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <h2 className="mt-2.5 font-serif text-2xl font-light text-foreground sm:text-3xl">
          {t("ourBeverages")}
        </h2>
      </div>

      {/* Grelha de categorias */}
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-3 px-4 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 sm:px-6">
        {categoriesBebidas.map((category, index) => {
          const Icon = drinkCategoryIcon(category.nome)
          const capa = category.urls?.[0]
          return (
            <motion.button
              key={category.nome}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.25) }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setCategoriaAberta(category.nome)}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-all hover:border-primary/50 hover:shadow-lg"
            >
              {/* Capa */}
              <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                {capa && !capa.includes("placeholder") ? (
                  <img
                    src={capa}
                    alt={category.nome}
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.style.display = "none"
                    }}
                  />
                ) : null}
                <div
                  className={`absolute inset-0 flex items-center justify-center ${capa && !capa.includes("placeholder") ? "opacity-0" : ""}`}
                  style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.14), hsl(var(--primary) / 0.04))" }}
                >
                  <Icon className="size-9 text-primary/60" aria-hidden="true" />
                </div>

                {/* Contagem */}
                <span className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-primary shadow-sm backdrop-blur">
                  {category.pratos.length}
                </span>
              </div>

              {/* Corpo */}
              <div className="flex items-center gap-2 p-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground group-hover:text-primary">
                    {category.nome}
                  </h3>
                  <p className="truncate text-[0.7rem] text-muted-foreground">
                    {category.pratos.length} {t("items")}
                  </p>
                </div>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden="true"
                />
              </div>
            </motion.button>
          )
        })}
      </div>

      {/* Vidro da categoria aberta */}
      <AnimatePresence>
        {categoriaAtiva && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] flex items-end justify-center bg-black/60 backdrop-blur-md sm:items-center sm:p-6"
            onClick={() => setCategoriaAberta(null)}
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: "spring", damping: 27, stiffness: 280 }}
              className="max-h-[88vh] w-full overflow-hidden rounded-t-3xl bg-card text-card-foreground shadow-2xl sm:max-w-xl sm:rounded-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cabeçalho do vidro */}
              <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-card px-5 py-4">
                <span className="flex size-10 items-center justify-center rounded-xl text-white shadow"
                  style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))" }}>
                  {(() => {
                    const Icon = drinkCategoryIcon(categoriaAtiva.nome)
                    return <Icon className="size-5" aria-hidden="true" />
                  })()}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-serif text-xl font-light">{categoriaAtiva.nome}</h3>
                  <p className="text-xs text-muted-foreground">
                    {categoriaAtiva.pratos.length} {t("items")}
                  </p>
                </div>
                <button
                  onClick={() => setCategoriaAberta(null)}
                  className="rounded-full p-2 transition-colors hover:bg-muted"
                  aria-label="Fechar"
                >
                  <X className="size-4" />
                </button>
              </div>

              {/* Itens */}
              <div className="max-h-[60vh] space-y-0.5 overflow-y-auto p-2 safe-bottom">
                {categoriaAtiva.pratos.map((item, i) => {
                  const ItemIcon = drinkItemIcon(item.nome)
                  const thumb = item.urls?.[0]
                  const qty = qtyPorBebida.get(item._id) || 0
                  return (
                    <motion.div
                      key={item._id || i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.04, 0.25) }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedDish(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          setSelectedDish(item)
                        }
                      }}
                      className={`group flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 transition-colors ${
                        qty > 0
                          ? "border border-primary/30 bg-primary/5"
                          : "hover:bg-muted/60"
                      }`}
                    >
                      <div className="relative size-10 shrink-0 overflow-hidden rounded-lg bg-secondary">
                        {thumb && !thumb.includes("placeholder") ? (
                          <img
                            src={thumb}
                            alt={item.nome}
                            loading="lazy"
                            className="size-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none"
                            }}
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center text-muted-foreground/40">
                            <ItemIcon className="size-4" aria-hidden="true" />
                          </div>
                        )}
                        {qty > 0 && (
                          <motion.span
                            key={qty}
                            initial={{ scale: 0.4 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 20 }}
                            className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[0.6rem] font-bold text-white"
                          >
                            {qty}
                          </motion.span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium group-hover:text-primary">{item.nome}</p>
                        <p className="truncate text-xs font-serif text-primary">
                          {formatPrice(effectivePrice(item))}
                          {item.descricao && (
                            <span className="ml-1.5 font-sans text-[0.7rem] text-muted-foreground">
                              · {item.descricao}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Stepper compacto: − quantidade + */}
                      <div
                        className="flex shrink-0 items-center gap-0.5 rounded-full border border-border bg-card p-0.5 shadow-sm"
                        role="group"
                        aria-label={`Quantidade de ${item.nome}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AnimatePresence mode="popLayout" initial={false}>
                          {qty > 0 && (
                            <>
                              <motion.button
                                key="menos"
                                type="button"
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: 26 }}
                                exit={{ opacity: 0, width: 0 }}
                                aria-label={`Diminuir quantidade de ${item.nome}`}
                                onClick={() => diminuir(item)}
                                className="flex h-7 items-center justify-center overflow-hidden rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                              >
                                <Minus className="size-3.5" />
                              </motion.button>
                              <span
                                key="qtd"
                                aria-live="polite"
                                className="w-4 min-w-4 text-center text-xs font-bold text-primary"
                              >
                                {qty}
                              </span>
                            </>
                          )}
                        </AnimatePresence>
                        <button
                          type="button"
                          aria-label={`${t("addToDraft")}: ${item.nome}`}
                          onClick={() => aumentar(item)}
                          className="flex size-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform hover:scale-105 active:scale-95"
                          style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))" }}
                        >
                          <Plus className="size-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de detalhes da bebida — sempre por cima da lista aberta */}
      {selectedDish && (
        <DishDetailModal
          dish={selectedDish}
          zClassName="z-[75]"
          onClose={() => setTimeout(() => setSelectedDish(null), 300)}
        />
      )}
    </section>
  )
}
